import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { fmtHora as fmtTime, fmtDataCurta as fmtFull } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FrentistaPanel() {
  const [data, setData] = useState({ ativos: [], historico: [] });
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [trackingId, setTrackingId] = useState(null);  // frete em transmissão GPS
  const [lastSent, setLastSent] = useState(null);
  const [gpsErr, setGpsErr] = useState(null);
  // Fotos por frete em buffer local: { [freteId]: { retirada: [urls], entrega: [urls] } }
  const [fotosByFrete, setFotosByFrete] = useState({});
  const [uploadingId, setUploadingId] = useState(null);  // "freteId-tipo"
  const watchRef = useRef(null);

  const token = localStorage.getItem("cps_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/fretes/frentista/me`, { headers });
      setData(r.data || { ativos: [], historico: [] });
      // sincroniza fotos do servidor pra o buffer local
      const allFretes = [...(r.data?.ativos || []), ...(r.data?.historico || [])];
      const map = {};
      allFretes.forEach(f => {
        map[f.id] = {
          retirada: f.fotos_retirada || [],
          entrega: f.fotos_entrega || [],
        };
      });
      setFotosByFrete(map);
      const transit = (r.data?.ativos || []).find(f => f.status === "em_transito");
      if (transit) setTrackingId(transit.id);
      else setTrackingId(null);
    } catch (e) {
      console.error("[Frentista] load err:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // pega info do usuário logado
    axios.get(`${API}/auth/me`, { headers })
      .then(r => setMe(r.data))
      .catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // GPS watcher — start quando trackingId muda para um frete válido
  useEffect(() => {
    if (!trackingId) {
      if (watchRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
      return;
    }
    if (!navigator.geolocation) {
      setGpsErr("Este dispositivo não tem GPS disponível.");
      return;
    }
    setGpsErr(null);
    const sendCoord = async (lat, lng) => {
      try {
        await axios.post(`${API}/fretes/${trackingId}/location`, { lat, lng }, { headers });
        setLastSent(new Date());
      } catch (e) {
        console.warn("[Frentista] location POST falhou:", e?.response?.status);
        // se 409 (frete não em transito), recarrega lista
        if (e?.response?.status === 409) load();
      }
    };
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => sendCoord(pos.coords.latitude, pos.coords.longitude),
      (err) => setGpsErr(`GPS: ${err.message}`),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 }
    );
    // backup: a cada 15s força nova posição (pra continuar transmitindo
    // mesmo quando o tablet não detecta mudança)
    const ticker = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => sendCoord(pos.coords.latitude, pos.coords.longitude),
        () => {},
        { enableHighAccuracy: true, maximumAge: 8000, timeout: 25000 }
      );
    }, 15000);
    return () => {
      if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
      clearInterval(ticker);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingId]);

  const uploadFotos = async (freteId, tipo, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const key = `${freteId}-${tipo}`;
    setUploadingId(key);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      files.forEach(f => fd.append("files", f));
      const r = await axios.post(`${API}/fretes/${freteId}/fotos`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const novasFotos = r.data?.fotos || [];
      setFotosByFrete(prev => ({
        ...prev,
        [freteId]: {
          ...(prev[freteId] || { retirada: [], entrega: [] }),
          [tipo]: [...((prev[freteId] || {})[tipo] || []), ...novasFotos],
        },
      }));
    } catch (e) {
      alert("Erro ao enviar fotos: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploadingId(null);
    }
  };

  const confirmarSaida = async (frete) => {
    if (busyId) return;
    const fotos = (fotosByFrete[frete.id] || {}).retirada || [];
    if (fotos.length === 0) {
      alert("📷 Tire pelo menos UMA foto do material antes de confirmar a saída.");
      return;
    }
    if (!window.confirm(`Confirmar SAÍDA para ${frete.obra_nome}?\n\nO GPS do tablet começará a transmitir sua localização.`)) return;
    setBusyId(frete.id);
    try {
      // Força permissão de GPS antes (UX: pergunta antes de marcar saída)
      if (navigator.geolocation) {
        try {
          await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej,
              { enableHighAccuracy: true, timeout: 15000 }
            )
          );
        } catch (e) {
          if (!window.confirm("Não foi possível ler o GPS. Continuar mesmo assim?")) {
            setBusyId(null);
            return;
          }
        }
      }
      await axios.post(`${API}/fretes/${frete.id}/saida`, {}, { headers });
      await load();
      alert("✅ Saída confirmada! Transmissão GPS ativada.");
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusyId(null);
    }
  };

  const confirmarChegada = async (frete) => {
    if (busyId) return;
    const fotos = (fotosByFrete[frete.id] || {}).entrega || [];
    if (fotos.length === 0) {
      alert("📷 Tire pelo menos UMA foto da entrega antes de confirmar a chegada.");
      return;
    }
    if (!window.confirm(`Confirmar CHEGADA em ${frete.obra_nome}?\n\nA transmissão GPS será encerrada.`)) return;
    setBusyId(frete.id);
    try {
      await axios.post(`${API}/fretes/${frete.id}/chegada`, {}, { headers });
      await load();
      alert("🏁 Chegada confirmada! Entrega registrada.");
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div style={styles.container}><div style={{ padding: 40, textAlign: "center", color: "#8a857a" }}>Carregando…</div></div>;

  return (
    <div style={styles.container} data-testid="frentista-panel">
      <header style={styles.header}>
        <div>
          <div style={{ fontFamily: "'Fraunces',serif", color: "#b8935a", fontSize: 26, letterSpacing: 2 }}>CAMASA</div>
          <div style={{ fontSize: 11, color: "#8a857a", letterSpacing: 2, textTransform: "uppercase" }}>Instalador em Obra</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 14, color: "#f5f0e8" }}>{me?.nome || me?.name || "Frentista"}</div>
          <button
            onClick={() => { localStorage.removeItem("cps_token"); window.location.href = "/login"; }}
            style={styles.logoutBtn}
            data-testid="frentista-logout"
          >Sair</button>
        </div>
      </header>

      {/* Indicador de transmissão GPS ativo */}
      {trackingId && (
        <div style={styles.gpsBanner} data-testid="frentista-gps-banner">
          <span style={styles.pulse}></span>
          <div>
            <div style={{ fontSize: 13, color: "#b8935a", fontWeight: 600 }}>📡 Transmitindo localização ao vivo</div>
            <div style={{ fontSize: 11, color: "#8a857a", marginTop: 2 }}>
              {lastSent ? `Última atualização: ${fmtTime(lastSent.toISOString())}` : "Capturando GPS…"}
              {gpsErr && <span style={{ color: "#e35d5d", marginLeft: 8 }}>· {gpsErr}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Entregas Ativas */}
      <section>
        <h2 style={styles.sectionTitle}>Entregas em andamento</h2>
        {data.ativos.length === 0 && (
          <div style={styles.emptyBox} data-testid="frentista-empty-ativos">
            Nenhuma entrega no momento. Aguarde a alocação de uma nova rota.
          </div>
        )}
        {data.ativos.map(f => {
          const isTransit = f.status === "em_transito";
          return (
            <div key={f.id} style={{ ...styles.fretCard, ...(isTransit ? styles.fretCardActive : {}) }} data-testid={`frente-card-${f.id}`}>
              <div style={styles.fretHeader}>
                <div>
                  <div style={{ fontSize: 12, color: "#8a857a", letterSpacing: 1, textTransform: "uppercase" }}>{f.obra_codigo || "Obra"}</div>
                  <div style={{ fontSize: 18, color: "#f5f0e8", fontWeight: 500, marginTop: 2 }}>{f.obra_nome}</div>
                </div>
                <span style={styles.statusPill(f.status)}>{labelStatus(f.status)}</span>
              </div>

              {f.endereco_entrega && (
                <div style={styles.fretRow}>
                  <span style={styles.rowLabel}>📍 Endereço</span>
                  <span style={styles.rowValue}>{f.endereco_entrega}</span>
                </div>
              )}

              {(f.materiais || []).length > 0 && (
                <div style={styles.fretRow}>
                  <span style={styles.rowLabel}>📦 Materiais</span>
                  <ul style={styles.materiaisList}>
                    {f.materiais.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}

              {isTransit && f.saida_em && (
                <div style={styles.fretRow}>
                  <span style={styles.rowLabel}>🕐 Saída</span>
                  <span style={styles.rowValue}>{fmtFull(f.saida_em)}</span>
                </div>
              )}

              {/* Botão de ação */}
              {f.status === "pendente" && (
                <>
                  <FotoSection
                    tipo="retirada"
                    label="📷 TIRAR FOTOS DO MATERIAL"
                    fotos={(fotosByFrete[f.id] || {}).retirada || []}
                    onUpload={(fl) => uploadFotos(f.id, "retirada", fl)}
                    uploading={uploadingId === `${f.id}-retirada`}
                    freteId={f.id}
                  />
                  <button
                    onClick={() => confirmarSaida(f)}
                    disabled={busyId === f.id}
                    style={styles.bigBtnGreen}
                    data-testid={`frente-saida-${f.id}`}
                  >
                    {busyId === f.id ? "Aguarde…" : "✅ CONFIRMAR SAÍDA"}
                  </button>
                </>
              )}
              {isTransit && (
                <>
                  <FotoSection
                    tipo="entrega"
                    label="📷 TIRAR FOTOS DA ENTREGA"
                    fotos={(fotosByFrete[f.id] || {}).entrega || []}
                    onUpload={(fl) => uploadFotos(f.id, "entrega", fl)}
                    uploading={uploadingId === `${f.id}-entrega`}
                    freteId={f.id}
                  />
                  <button
                    onClick={() => confirmarChegada(f)}
                    disabled={busyId === f.id}
                    style={styles.bigBtnRed}
                    data-testid={`frente-chegada-${f.id}`}
                  >
                    {busyId === f.id ? "Aguarde…" : "🏁 CONFIRMAR CHEGADA"}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </section>

      {/* Histórico */}
      {data.historico.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h2 style={styles.sectionTitle}>Histórico ({data.historico.length})</h2>
          {data.historico.slice(0, 20).map(f => (
            <div key={f.id} style={styles.histCard} data-testid={`frente-hist-${f.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: "#8a857a", letterSpacing: 1, textTransform: "uppercase" }}>{f.obra_codigo}</div>
                  <div style={{ fontSize: 14, color: "#f5f0e8" }}>{f.obra_nome}</div>
                </div>
                <span style={styles.statusPill(f.status)}>{labelStatus(f.status)}</span>
              </div>
              <div style={{ fontSize: 12, color: "#8a857a", marginTop: 6, display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span>🚚 Saída {fmtFull(f.saida_em)}</span>
                {f.chegada_em && <span>🏁 Chegada {fmtFull(f.chegada_em)}</span>}
              </div>
            </div>
          ))}
        </section>
      )}

      <style>{`
        @keyframes pulse-frente {
          0% { box-shadow: 0 0 0 0 rgba(184,147,90,0.7); }
          70% { box-shadow: 0 0 0 12px rgba(184,147,90,0); }
          100% { box-shadow: 0 0 0 0 rgba(184,147,90,0); }
        }
      `}</style>
    </div>
  );
}

const labelStatus = (s) => {
  if (s === "pendente") return "AGUARDANDO";
  if (s === "em_transito") return "EM TRÂNSITO";
  if (s === "entregue") return "ENTREGUE";
  if (s === "cancelado") return "CANCELADO";
  return s;
};

function FotoSection({ tipo, label, fotos, onUpload, uploading, freteId }) {
  const inputRef = useRef(null);
  const inputId = `frete-${tipo}-input-${freteId}`;
  return (
    <div style={fotoSectionStyles.wrap} data-testid={`foto-section-${tipo}-${freteId}`}>
      <div style={fotoSectionStyles.headerRow}>
        <div style={fotoSectionStyles.label}>{label}</div>
        {fotos.length > 0 && (
          <span style={fotoSectionStyles.count} data-testid={`foto-count-${tipo}-${freteId}`}>
            ✓ {fotos.length} foto{fotos.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          onUpload(e.target.files);
          e.target.value = ""; // permite re-tirar mesma foto
        }}
        data-testid={`foto-input-${tipo}-${freteId}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        style={fotoSectionStyles.btn}
        data-testid={`foto-btn-${tipo}-${freteId}`}
      >
        {uploading ? "Enviando…" : (fotos.length === 0 ? "📷 TIRAR FOTO" : "+ ADICIONAR MAIS FOTOS")}
      </button>
      {fotos.length > 0 && (
        <div style={fotoSectionStyles.thumbs}>
          {fotos.map((url, i) => (
            <div key={i} style={fotoSectionStyles.thumb}>
              <img src={url} alt={`${tipo} ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const fotoSectionStyles = {
  wrap: {
    marginTop: 14,
    padding: 12,
    background: "rgba(184,147,90,0.04)",
    border: "1px dashed rgba(184,147,90,0.3)",
    borderRadius: 5,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  label: {
    fontSize: 11,
    letterSpacing: 1.5,
    color: "#8a857a",
    textTransform: "uppercase",
    fontWeight: 600,
  },
  count: {
    fontSize: 11,
    color: "#5fa86c",
    fontWeight: 600,
    background: "rgba(95,168,108,0.1)",
    padding: "3px 8px",
    borderRadius: 3,
  },
  btn: {
    width: "100%",
    background: "transparent",
    color: "#b8935a",
    border: "1px solid #b8935a",
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 1,
    cursor: "pointer",
    borderRadius: 4,
  },
  thumbs: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))",
    gap: 6,
    marginTop: 10,
  },
  thumb: {
    aspectRatio: "1",
    overflow: "hidden",
    borderRadius: 4,
    border: "1px solid #2b2833",
    background: "#0d0d0f",
  },
};

const styles = {
  container: {
    maxWidth: 760,
    margin: "0 auto",
    padding: "20px 16px 80px",
    fontFamily: "'Helvetica Neue',Arial,sans-serif",
    color: "#cdc8be",
    background: "#0d0d0f",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 16,
    borderBottom: "1px solid #2b2833",
    marginBottom: 20,
    gap: 12,
    flexWrap: "wrap",
  },
  logoutBtn: {
    marginTop: 6,
    background: "transparent",
    border: "1px solid #2b2833",
    color: "#8a857a",
    padding: "4px 10px",
    fontSize: 11,
    cursor: "pointer",
    borderRadius: 3,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  gpsBanner: {
    background: "rgba(184,147,90,0.08)",
    border: "1px solid rgba(184,147,90,0.3)",
    borderRadius: 6,
    padding: 14,
    marginBottom: 18,
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  pulse: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: "#b8935a",
    animation: "pulse-frente 1.6s infinite",
    display: "inline-block",
    flexShrink: 0,
  },
  sectionTitle: {
    fontFamily: "'Fraunces',serif",
    fontSize: 18,
    fontWeight: 400,
    color: "#f5f0e8",
    letterSpacing: 0.5,
    margin: "0 0 14px",
  },
  emptyBox: {
    background: "#15141a",
    border: "1px dashed #2b2833",
    padding: 32,
    textAlign: "center",
    color: "#8a857a",
    borderRadius: 6,
    fontSize: 13,
  },
  fretCard: {
    background: "#15141a",
    border: "1px solid #2b2833",
    padding: 18,
    borderRadius: 6,
    marginBottom: 14,
  },
  fretCardActive: {
    border: "1px solid rgba(184,147,90,0.4)",
    boxShadow: "0 4px 18px rgba(184,147,90,0.08)",
  },
  fretHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 14,
    gap: 8,
  },
  statusPill: (s) => ({
    fontSize: 10,
    letterSpacing: 1.5,
    padding: "3px 9px",
    borderRadius: 3,
    fontWeight: 600,
    background: s === "em_transito" ? "rgba(184,147,90,0.15)"
              : s === "entregue" ? "rgba(95,168,108,0.15)"
              : s === "cancelado" ? "rgba(227,93,93,0.15)"
              : "rgba(205,200,190,0.08)",
    color: s === "em_transito" ? "#b8935a"
         : s === "entregue" ? "#5fa86c"
         : s === "cancelado" ? "#e35d5d"
         : "#cdc8be",
    border: `1px solid ${
      s === "em_transito" ? "#b8935a"
      : s === "entregue" ? "#5fa86c"
      : s === "cancelado" ? "#e35d5d"
      : "#2b2833"
    }`,
  }),
  fretRow: {
    display: "flex",
    gap: 12,
    padding: "8px 0",
    borderTop: "1px solid #2b2833",
    fontSize: 13,
  },
  rowLabel: { color: "#8a857a", minWidth: 92, fontSize: 12 },
  rowValue: { color: "#f5f0e8", flex: 1 },
  materiaisList: { margin: 0, padding: "0 0 0 16px", color: "#cdc8be", flex: 1, fontSize: 13, lineHeight: 1.6 },
  bigBtnGreen: {
    width: "100%",
    background: "#5fa86c",
    color: "#0d0d0f",
    border: "none",
    padding: "16px 20px",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 1,
    cursor: "pointer",
    borderRadius: 4,
    marginTop: 14,
    transition: "transform 0.1s",
  },
  bigBtnRed: {
    width: "100%",
    background: "#e35d5d",
    color: "#0d0d0f",
    border: "none",
    padding: "16px 20px",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 1,
    cursor: "pointer",
    borderRadius: 4,
    marginTop: 14,
  },
  histCard: {
    background: "#15141a",
    border: "1px solid #2b2833",
    padding: 12,
    borderRadius: 4,
    marginBottom: 8,
  },
};
